/**
 * `<SettingsMembersRoute>` — `/w/:slug/settings/members`
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 5.3`
 * + `functional-design/frontend-components.md § <MembersSettings>`.
 *
 * Combined members + invitations view per ND4-F1. Two ruled tables:
 *   1. Pending invitations (rendered only when non-empty)
 *   2. Active members
 *
 * Action gating via `<Can>` + `useAbility()`:
 *   - `invite` Member → "Invite member" button
 *   - `update-role` Member → role cell becomes a `<Select>`
 *   - `remove` Member → actions cell shows remove
 *   - `revoke` Invitation → revoke button on pending row
 */

import { Can, authClient, useAbility, useAuth, useWorkspace } from "@cometchat-ai/auth/client";
import type { PublicInvitation, PublicMember } from "@cometchat-ai/auth/client";
import { isHelpdeskBuiltinRole } from "@cometchat-ai/auth/shared";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableBody,
  TableHeader,
} from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { Mail, MoreHorizontal, Plus, Trash2, Users } from "../components/icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { RolePill } from "../components/settings-parts";
import { listRoles, rolesKeys } from "../settings/api";
import { InviteMemberDialog } from "../components/invite-member-dialog";
import { RemoveMemberDialog } from "../components/remove-member-dialog";
import { RevokeInvitationDialog } from "../components/revoke-invitation-dialog";
import { RoleSelect } from "../components/role-select";

/**
 * Label for any workspace role value: a custom `RoleDefinition.id` resolves to
 * its name (via `customNames`), the two core built-ins use their passed i18n
 * strings, and fine-grained built-ins fall back to a humanized form.
 */
function roleLabel(
  role: string,
  adminLabel: string,
  memberLabel: string,
  customNames?: ReadonlyMap<string, string>,
): string {
  const custom = customNames?.get(role);
  if (custom) return custom;
  if (role === "admin") return adminLabel;
  if (role === "member") return memberLabel;
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ");
}

function memberName(member: PublicMember): string | null {
  const name = `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
  return name.length > 0 ? name : null;
}

/** The tier a role belongs to: helpdesk built-ins + helpdesk-scoped custom
 * roles are `helpdesk`; everything else (admin/member/fine-grained built-ins +
 * workspace-scoped customs) is `workspace`. */
function roleScope(
  role: string,
  customScopes: ReadonlyMap<string, "workspace" | "helpdesk">,
): "workspace" | "helpdesk" {
  if (isHelpdeskBuiltinRole(role)) return "helpdesk";
  return customScopes.get(role) ?? "workspace";
}

/** Small caption marking which tier a member's role belongs to. Shown on every
 * row so admins can tell workspace roles from desk roles at a glance. */
function ScopeTag({ scope }: { scope: "workspace" | "helpdesk" }) {
  const helpdesk = scope === "helpdesk";
  // Fixed width + centered text so every pill in the Scope column is the same
  // size and reads as a clean stack. `flex` (block-level, not inline-flex) so
  // the align-middle table cell also centers it vertically.
  const base =
    "flex w-[88px] items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium";
  return (
    <span
      className={
        helpdesk
          ? `${base} text-primary [background:color-mix(in_oklch,var(--color-primary)_12%,transparent)]`
          : `${base} bg-muted text-muted-foreground`
      }
    >
      {helpdesk ? "Helpdesk" : "Workspace"}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function SectionHeading({ icon: Icon, title }: { icon: typeof Users; title: string }) {
  return (
    <div className="flex items-center gap-2.5 px-6 pb-3 pt-1">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <h3 className="text-sm font-semibold tracking-[0.01em] text-foreground">{title}</h3>
    </div>
  );
}

export function SettingsMembersRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const ability = useAbility();
  const auth = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PublicMember | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PublicInvitation | null>(null);

  const membersQuery = useQuery({
    queryKey: ["members", workspace.slug],
    queryFn: () => authClient.listMembers(workspace.slug),
  });
  const invitationsQuery = useQuery({
    queryKey: ["invitations", workspace.slug],
    queryFn: () => authClient.listPendingInvitations(workspace.slug),
    enabled: ability.can("read", "Invitation"),
  });
  // Resolve custom-role ids → names so member + invite pills read nicely.
  const rolesQuery = useQuery({
    queryKey: rolesKeys.list(workspace.slug),
    queryFn: () => listRoles(workspace.slug),
    staleTime: 30_000,
  });
  const customRoleNames = useMemo(
    () => new Map((rolesQuery.data?.custom ?? []).map((c) => [c.id, c.name] as const)),
    [rolesQuery.data],
  );
  const customRoleScopes = useMemo(
    () =>
      new Map((rolesQuery.data?.custom ?? []).map((c) => [c.id, c.scope ?? "workspace"] as const)),
    [rolesQuery.data],
  );

  const canChangeRole = ability.can("update-role", "Member");
  const canRemove = ability.can("remove", "Member");
  const canRevoke = ability.can("revoke", "Invitation");

  const members = membersQuery.data ?? [];
  const invitations = invitationsQuery.data ?? [];
  const memberCount = members.length;
  const inviteCount = invitations.length;

  const activeTitle = useMemo(
    () =>
      memberCount === 1
        ? t("members.activeTitleOne")
        : t("members.activeTitleOther", { count: memberCount }),
    [memberCount, t],
  );
  const pendingTitle = useMemo(
    () =>
      inviteCount === 1
        ? t("members.pendingTitleOne")
        : t("members.pendingTitleOther", { count: inviteCount }),
    [inviteCount, t],
  );

  return (
    <PageShell
      className="p-0"
      data-testid="settings-members"
      description={t("members.subtitle")}
      actions={
        <Can I="invite" a="Member">
          <Button
            onClick={() => setInviteOpen(true)}
            size="sm"
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            data-testid="settings-members-invite"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t("members.invite")}
          </Button>
        </Can>
      }
    >
      <div className="flex flex-col py-2">
        {invitations.length > 0 ? (
          <div className="pb-4" data-testid="settings-members-pending">
            <SectionHeading icon={Mail} title={pendingTitle} />
            <DataGrid className="border-t border-border">
              <TableHeader>
                <DataGridHeaderRow>
                  <DataGridHead className="pl-6">{t("members.table.email")}</DataGridHead>
                  <DataGridHead className="w-[140px]">{t("members.table.role")}</DataGridHead>
                  <DataGridHead className="w-[110px]">{t("members.table.scope")}</DataGridHead>
                  <DataGridHead className="w-[230px]">{t("members.table.invitedBy")}</DataGridHead>
                  <DataGridHead className="w-[140px] pr-6" divider={canRevoke}>
                    {t("members.table.expires")}
                  </DataGridHead>
                  {canRevoke ? (
                    <DataGridHead className="w-[100px] pr-6 text-right" divider={false}>
                      {t("members.table.actions")}
                    </DataGridHead>
                  ) : null}
                </DataGridHeaderRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <DataGridRow
                    key={invitation.id}
                    data-testid={`pending-invite-row-${invitation.id}`}
                  >
                    <DataGridCell className="truncate pl-6 text-foreground">
                      {invitation.email}
                    </DataGridCell>
                    <DataGridCell>
                      <RolePill
                        role={invitation.role}
                        label={roleLabel(
                          invitation.role,
                          t("members.roles.admin"),
                          t("members.roles.member"),
                          customRoleNames,
                        )}
                      />
                    </DataGridCell>
                    <DataGridCell>
                      <ScopeTag scope={roleScope(invitation.role, customRoleScopes)} />
                    </DataGridCell>
                    <DataGridCell className="truncate">{invitation.invitedBy.email}</DataGridCell>
                    <DataGridCell className="whitespace-nowrap pr-6" divider={canRevoke}>
                      {formatDate(invitation.expiresAt)}
                    </DataGridCell>
                    {canRevoke ? (
                      <DataGridCell className="pr-6 text-right" divider={false}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={t("members.table.actions")}
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                              data-testid={`pending-invite-actions-${invitation.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
                              onClick={() => setRevokeTarget(invitation)}
                              data-testid={`pending-invite-revoke-${invitation.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                              {t("members.revoke.trigger")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </DataGridCell>
                    ) : null}
                  </DataGridRow>
                ))}
              </TableBody>
            </DataGrid>
          </div>
        ) : null}

        <SectionHeading icon={Users} title={activeTitle} />
        {membersQuery.isPending ? (
          <ListSkeleton
            rows={3}
            itemClassName="h-10 w-full"
            className="space-y-2 px-6"
            data-testid="members-skeleton"
          />
        ) : membersQuery.error ? (
          <div className="px-6">
            <ErrorState message={membersQuery.error.message} onRetry={membersQuery.refetch} />
          </div>
        ) : members.length === 0 ? (
          <div className="px-6 py-12">
            <EmptyState data-testid="members-empty" icon={Users} title={t("members.empty")} />
          </div>
        ) : (
          <DataGrid className="border-t border-border">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6">{t("members.table.user")}</DataGridHead>
                <DataGridHead className="w-[180px]">{t("members.table.role")}</DataGridHead>
                <DataGridHead className="w-[110px]">{t("members.table.scope")}</DataGridHead>
                <DataGridHead className="w-[160px] pr-6" divider={canRemove}>
                  {t("members.table.joined")}
                </DataGridHead>
                {canRemove ? (
                  <DataGridHead className="w-[100px] pr-6 text-right" divider={false}>
                    {t("members.table.actions")}
                  </DataGridHead>
                ) : null}
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const isSelf = member.email === auth.user?.email;
                const name = memberName(member);
                return (
                  <DataGridRow key={member.id} data-testid={`member-row-${member.id}`}>
                    <DataGridCell className="pl-6">
                      <div className="min-w-0">
                        {name ? (
                          <div className="truncate text-[13px] text-foreground">{name}</div>
                        ) : null}
                        <div className="truncate text-[13px] text-muted-foreground">
                          {member.email}
                        </div>
                      </div>
                    </DataGridCell>
                    <DataGridCell>
                      {canChangeRole && !isSelf ? (
                        <RoleSelect
                          workspaceSlug={workspace.slug}
                          memberId={member.id}
                          currentRole={member.role}
                        />
                      ) : (
                        <RolePill
                          role={member.role}
                          label={roleLabel(
                            member.role,
                            t("members.roles.admin"),
                            t("members.roles.member"),
                            customRoleNames,
                          )}
                        />
                      )}
                    </DataGridCell>
                    <DataGridCell>
                      <ScopeTag scope={roleScope(member.role, customRoleScopes)} />
                    </DataGridCell>
                    <DataGridCell className="whitespace-nowrap pr-6" divider={canRemove}>
                      {formatDate(member.joinedAt)}
                    </DataGridCell>
                    {canRemove ? (
                      <DataGridCell className="pr-6 text-right" divider={false}>
                        {!isSelf ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label={t("members.table.actions")}
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                                data-testid={`member-actions-${member.id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem
                                className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
                                onClick={() => setRemoveTarget(member)}
                                data-testid={`member-remove-${member.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                {t("members.remove.trigger")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </DataGridCell>
                    ) : null}
                  </DataGridRow>
                );
              })}
            </TableBody>
          </DataGrid>
        )}
      </div>

      <InviteMemberDialog
        workspaceSlug={workspace.slug}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
      />
      {removeTarget ? (
        <RemoveMemberDialog
          workspaceSlug={workspace.slug}
          memberId={removeTarget.id}
          memberEmail={removeTarget.email}
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
        />
      ) : null}
      {revokeTarget ? (
        <RevokeInvitationDialog
          workspaceSlug={workspace.slug}
          invitationId={revokeTarget.id}
          invitationEmail={revokeTarget.email}
          open={revokeTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRevokeTarget(null);
          }}
        />
      ) : null}
    </PageShell>
  );
}
